import React, { useState, useRef, useCallback, useEffect } from 'react'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import CloseIcon from '@material-ui/icons/Close'
import axios from 'axios'

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(46, 125, 50, 0.85)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  transition: 'opacity 0.2s ease'
}

const overlayTextStyle = {
  color: '#fff',
  fontSize: 28,
  fontWeight: 600,
  marginTop: 16
}

const overlaySubTextStyle = {
  color: 'rgba(255,255,255,0.8)',
  fontSize: 14,
  marginTop: 8
}

const overlayIconStyle = {
  color: '#fff',
  fontSize: 64
}

// ── Background progress pill ─────────────────────────────────────
// Fixed to bottom-right, shows upload + parse progress, auto-dismisses.
const progressPillStyle = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 1400,
  backgroundColor: '#fff',
  borderRadius: 12,
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  padding: '12px 16px',
  minWidth: 300,
  maxWidth: 400
}

const emitDocumentUploaded = (projectId, payload) => {
  if (!projectId || !payload || typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return
  window.dispatchEvent(new CustomEvent('bid-estimation-document-uploaded', {
    detail: { projectId: String(projectId), payload }
  }))
}

const CATEGORY_OPTIONS = [
  { key: 'plans', label: 'Plans' },
  { key: 'bid_response', label: 'Bid Response' },
  { key: 'vendor_quote', label: 'Vendor Quote' },
  { key: 'photos', label: 'Photos' },
  { key: 'vendor_invoice', label: 'Vendor Invoice' },
  { key: 'signed_estimate', label: 'Signed Estimate' },
  { key: 'files', label: 'Other Files' }
]

const CATEGORY_BY_KEY = CATEGORY_OPTIONS.reduce((acc, option) => {
  acc[option.key] = option
  return acc
}, {})

const splitFileName = (name = '') => {
  const cleaned = String(name || 'file').trim()
  const dot = cleaned.lastIndexOf('.')
  if (dot <= 0 || dot === cleaned.length - 1) return { base: cleaned || 'file', ext: '' }
  return { base: cleaned.slice(0, dot), ext: cleaned.slice(dot).toLowerCase() }
}

const slugifyName = value => String(value || '')
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 80)

const suggestCategory = file => {
  const name = String(file?.name || '').toLowerCase()
  const ext = splitFileName(name).ext
  if (['.jpg', '.jpeg', '.png', '.gif'].includes(ext)) return 'photos'
  if (/\b(invoice|inv)\b/.test(name)) return 'vendor_invoice'
  if (/\b(signed|executed|accepted|contract)\b/.test(name)) return 'signed_estimate'
  if (/\b(siteone|site one|ewing|horizon|sprinkler|supply|quote|quotation|estimate)\b/.test(name)) return 'vendor_quote'
  if (/\b(bid response|bid schedule|bid form|proposal|scope of work|sow)\b/.test(name)) return 'bid_response'
  if (/\b(plan|plans|drawing|drawings|sheet|sheets|addendum)\b/.test(name)) return 'plans'
  if (ext === '.pdf') return 'plans'
  return 'files'
}

const buildNormalizedName = (file, category, projectName) => {
  const { base, ext } = splitFileName(file?.name)
  const categorySlug = slugifyName(CATEGORY_BY_KEY[category]?.label || category || 'file')
  const projectSlug = slugifyName(projectName || 'project')
  const baseSlug = slugifyName(base) || 'document'
  return [categorySlug, projectSlug, baseSlug].filter(Boolean).join('_') + ext
}

const buildUploadItem = (file, projectName, index) => {
  const category = suggestCategory(file)
  return {
    id: `${Date.now()}-${index}-${file.name}`,
    file,
    category,
    displayName: buildNormalizedName(file, category, projectName),
    keepOriginalName: false
  }
}

const UploadDropzone = React.forwardRef(({ projectId, url, onUploadComplete, projectName }, ref) => {
  const [dragOver, setDragOver] = useState(false)
  const [uploadItems, setUploadItems] = useState([])
  const [uploadingBatch, setUploadingBatch] = useState(false)
  const fileInputRef = useRef()
  const dragCounterRef = useRef(0)
  const quotePollRef = useRef(null)

  // Background upload state
  const [bgJob, setBgJob] = useState(null)
  // bgJob shape: { fileName, category, status, progress, error, result }
  // status: 'uploading' | 'analyzing' | 'classified' | 'done' | 'error'

  const clearQuotePoll = useCallback(() => {
    if (quotePollRef.current) {
      clearTimeout(quotePollRef.current)
      quotePollRef.current = null
    }
  }, [])

  const pollVendorQuoteAutoMatch = useCallback((quoteId, isBatchUpload = false, attempt = 0) => {
    if (!quoteId) return
    clearQuotePoll()
    quotePollRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${url || ''}/api/bid-estimation/vendor-quotes/${quoteId}`)
        const status = data?.autoMatchStatus || 'complete'
        if (status === 'pending' && attempt < 60) {
          setBgJob(prev => prev && prev.quoteId === quoteId
            ? { ...prev, status: 'analyzing', message: 'Auto-matching quote line items...' }
            : prev)
          pollVendorQuoteAutoMatch(quoteId, isBatchUpload, attempt + 1)
          return
        }

        const lineItems = Array.isArray(data?.lineItems) ? data.lineItems : []
        const mappedCount = lineItems.filter(item => item.mappingStatus === 'mapped').length
        const reviewCount = lineItems.filter(item => item.mappingStatus === 'review').length
        const message = status === 'error'
          ? 'Quote parsed, but auto-match needs review.'
          : `Quote ready: ${mappedCount} matched${reviewCount ? `, ${reviewCount} need review` : ''}.`

        setBgJob(prev => prev && prev.quoteId === quoteId
          ? {
              ...prev,
              status: 'done',
              message,
              result: data,
              actionLabel: 'Review',
              actionPayload: { category: 'vendor_quote', action: 'open_quote_review', vendorQuote: data, isBatchUpload }
            }
          : prev)
      } catch (err) {
        setBgJob(prev => prev && prev.quoteId === quoteId
          ? {
              ...prev,
              status: 'done',
              message: 'Quote parsed. Open review to check matching.',
              actionLabel: 'Review',
              actionPayload: {
                category: 'vendor_quote',
                action: 'open_quote_review',
                vendorQuote: prev.result || { _id: quoteId },
                isBatchUpload
              }
            }
          : prev)
      }
    }, attempt === 0 ? 600 : 1500)
  }, [clearQuotePoll, url])

  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current += 1
    if (dragCounterRef.current === 1) setDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current -= 1
    if (dragCounterRef.current === 0) setDragOver(false)
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  // Start classifying the first file, queue the rest
  const startFileQueue = useCallback((files) => {
    if (!files || files.length === 0) return
    const arr = Array.from(files)
    setUploadItems(arr.map((file, index) => buildUploadItem(file, projectName, index)))
  }, [projectName])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
    dragCounterRef.current = 0
    const files = e.dataTransfer?.files
    if (files && files.length > 0) startFileQueue(files)
  }, [startFileQueue])

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  // Expose triggerUpload so parent/sibling components can open the file picker
  React.useImperativeHandle(ref, () => ({
    triggerUpload: handleButtonClick
  }))

  const handleFileSelect = (e) => {
    const files = e.target.files
    if (files && files.length > 0) startFileQueue(files)
    e.target.value = ''
  }

  const updateUploadItem = useCallback((id, patch) => {
    setUploadItems(prev => prev.map(item => {
      if (item.id !== id) return item
      const next = { ...item, ...patch }
      if (patch.category && !item.keepOriginalName && patch.displayName === undefined) {
        next.displayName = buildNormalizedName(item.file, patch.category, projectName)
      }
      return next
    }))
  }, [projectName])

  const removeUploadItem = useCallback((id) => {
    setUploadItems(prev => prev.filter(item => item.id !== id))
  }, [])

  const closeReviewModal = useCallback(() => {
    if (uploadingBatch) return
    setUploadItems([])
  }, [uploadingBatch])

  const uploadReviewedItem = useCallback(async (item, index, total) => {
    const { file, category } = item
    const isBatchUpload = total > 1
    const displayName = item.keepOriginalName ? file.name : (item.displayName || file.name)

    setBgJob({
      fileName: displayName,
      category,
      status: 'uploading',
      progress: 0,
      error: null,
      result: null,
      startTime: Date.now(),
      message: total > 1 ? `Uploading ${index + 1} of ${total}: ${displayName}` : null
    })

    const formData = new FormData()
    formData.append('file', file)
    formData.append('category', category)
    formData.append('displayName', displayName)
    formData.append('preserveOriginalName', item.keepOriginalName ? 'true' : 'false')
    if (category === 'bid_response') formData.append('name', displayName)

    const { data } = await axios.post(
      `${url || ''}/api/bid-estimation/projects/${projectId}/documents`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded / e.total) * 100)
            setBgJob(prev => prev ? { ...prev, progress: pct, status: pct < 100 ? 'uploading' : 'analyzing' } : prev)
          }
        }
      }
    )

    // Plans: classified → show page selector
    if (category === 'plans' && data.parseStatus === 'classified' && data.parseResult?.documentType === 'plan_set') {
      emitDocumentUploaded(projectId, data)
      if (onUploadComplete) onUploadComplete({ ...data, category, isBatchUpload })
      return
    }

    // Plans: small set fully parsed
    if (category === 'plans' && data.parseStatus === 'parsed' && data.parseResult?.documentType === 'plan_set') {
      emitDocumentUploaded(projectId, data)
      if (onUploadComplete) onUploadComplete({ ...data, category, isBatchUpload })
      return
    }

    // Vendor quote
    if (category === 'vendor_quote' && data.vendorQuote) {
      emitDocumentUploaded(projectId, data)
      const quoteId = data.vendorQuote._id
      setBgJob(prev => prev ? {
        ...prev,
        status: data.vendorQuote.autoMatchStatus === 'pending' ? 'analyzing' : 'done',
        progress: 100,
        message: data.vendorQuote.autoMatchStatus === 'pending'
          ? 'Quote parsed. Auto-matching line items...'
          : 'Quote parsed and ready for review.',
        result: data.vendorQuote,
        quoteId,
        actionLabel: data.vendorQuote.autoMatchStatus === 'pending' ? null : 'Review',
        actionPayload: {
          category: 'vendor_quote',
          action: 'open_quote_review',
          vendorQuote: data.vendorQuote,
          isBatchUpload
        }
      } : prev)
      if (onUploadComplete) onUploadComplete({ ...data, category, action: 'vendor_quote_uploaded', isBatchUpload })
      if (data.vendorQuote.autoMatchStatus === 'pending' && !isBatchUpload) {
        pollVendorQuoteAutoMatch(quoteId, isBatchUpload)
      }
      return
    }

    // Bid responses return a BidPackage, not a project document. Let the
    // dashboard refresh bid packages instead of appending it to Documents.
    if (category === 'bid_response') {
      const bidPackageId = data.bidPackageId || data.packageId || data._id
      if (onUploadComplete) {
        onUploadComplete({ ...data, category, action: isBatchUpload ? 'bid_response_uploaded' : 'open_bid_response', bidPackageId, isBatchUpload })
      }
      return
    }

    // Everything else
    emitDocumentUploaded(projectId, data)
    if (onUploadComplete) onUploadComplete({ ...data, category, isBatchUpload })
  }, [onUploadComplete, pollVendorQuoteAutoMatch, projectId, url])

  const uploadReviewedBatch = useCallback(async () => {
    if (uploadItems.length === 0 || uploadingBatch) return
    setUploadingBatch(true)
    const items = uploadItems
    setUploadItems([])
    try {
      for (let i = 0; i < items.length; i++) {
        await uploadReviewedItem(items[i], i, items.length)
      }
      setBgJob(prev => {
        if (!prev) return null
        if (items.length === 1 && prev.actionPayload) return prev
        return {
          ...prev,
          status: 'done',
          progress: 100,
          message: items.length > 1 ? `${items.length} documents uploaded.` : 'Upload complete.',
          actionLabel: null,
          actionPayload: null
        }
      })
    } catch (err) {
      setBgJob(prev => prev ? {
        ...prev,
        status: 'error',
        error: err.response?.data?.error || err.message || 'Upload failed'
      } : prev)
    } finally {
      setUploadingBatch(false)
    }
  }, [uploadItems, uploadingBatch, uploadReviewedItem])

  const handleParseStarted = ({ docId, pages, message }) => {
    setBgJob(prev => prev ? {
      ...prev,
      status: 'analyzing',
      result: prev.result || { _id: docId },
      progressMessage: message || `Extracting ${Array.isArray(pages) ? pages.length : 0} pages in background...`,
      message: message || `Extracting ${Array.isArray(pages) ? pages.length : 0} pages in background...`
    } : {
      fileName: 'Plan set',
      category: 'plans',
      status: 'analyzing',
      progress: 100,
      error: null,
      result: { _id: docId },
      progressMessage: message || `Extracting ${Array.isArray(pages) ? pages.length : 0} pages in background...`,
      message: message || `Extracting ${Array.isArray(pages) ? pages.length : 0} pages in background...`
    })
  }

  // Attach drag listeners to the whole document
  React.useEffect(() => {
    document.addEventListener('dragenter', handleDragEnter)
    document.addEventListener('dragleave', handleDragLeave)
    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('drop', handleDrop)
    return () => {
      document.removeEventListener('dragenter', handleDragEnter)
      document.removeEventListener('dragleave', handleDragLeave)
      document.removeEventListener('dragover', handleDragOver)
      document.removeEventListener('drop', handleDrop)
    }
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop])

  useEffect(() => () => clearQuotePoll(), [clearQuotePoll])

  const statusLabel = bgJob ? {
    uploading: `Uploading ${bgJob.fileName}…`,
    analyzing: bgJob.progressMessage || bgJob.message || `Analyzing ${bgJob.fileName}…`,
    classified: bgJob.message || 'Plan set classified',
    done: bgJob.message || 'Upload complete',
    error: bgJob.error || 'Upload failed'
  }[bgJob.status] : ''

  return (
    <>
      {/* Upload button for the header */}
      <Tooltip title="Upload File">
        <IconButton
          size="small"
          onClick={handleButtonClick}
          style={{ color: '#fff' }}
        >
          <CloudUploadIcon />
        </IconButton>
      </Tooltip>

      {/* Hidden file input — supports multiple files */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Drag overlay */}
      {dragOver && (
        <div style={overlayStyle}>
          <CloudUploadIcon style={overlayIconStyle} />
          <div style={overlayTextStyle}>Drop to Upload</div>
          <div style={overlaySubTextStyle}>Release to upload your file to this project</div>
        </div>
      )}

      <Dialog open={uploadItems.length > 0} maxWidth="md" fullWidth onClose={closeReviewModal}>
        <DialogTitle>
          Review Uploads
          <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
            {uploadItems.length} file{uploadItems.length !== 1 ? 's' : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {uploadItems.map((item, index) => {
              const categoryOption = CATEGORY_BY_KEY[item.category] || CATEGORY_BY_KEY.files
              const { ext } = splitFileName(item.file.name)
              return (
                <div
                  key={item.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(160px, 1fr) 210px minmax(220px, 1.2fr) 128px 28px',
                    gap: 10,
                    alignItems: 'center',
                    padding: '10px 0',
                    borderBottom: index < uploadItems.length - 1 ? '1px solid var(--line-1, #e0e0e0)' : 'none'
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <Typography variant="body2" style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.file.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {ext ? ext.replace('.', '').toUpperCase() : 'FILE'} · {(item.file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </div>
                  <Autocomplete
                    size="small"
                    options={CATEGORY_OPTIONS}
                    value={categoryOption}
                    getOptionLabel={option => option?.label || ''}
                    onChange={(event, option) => {
                      if (option?.key) updateUploadItem(item.id, { category: option.key })
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Category"
                      />
                    )}
                  />
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Document Name"
                    value={item.keepOriginalName ? item.file.name : item.displayName}
                    disabled={item.keepOriginalName}
                    onChange={event => updateUploadItem(item.id, { displayName: event.target.value })}
                  />
                  <FormControlLabel
                    style={{ margin: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={item.keepOriginalName}
                        onChange={event => updateUploadItem(item.id, { keepOriginalName: event.target.checked })}
                        color="primary"
                      />
                    }
                    label={<span style={{ fontSize: 12 }}>Do Not Rename</span>}
                  />
                  <IconButton size="small" onClick={() => removeUploadItem(item.id)}>
                    <CloseIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </div>
              )
            })}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReviewModal} disabled={uploadingBatch}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={uploadReviewedBatch}
            disabled={uploadingBatch || uploadItems.length === 0}
            startIcon={uploadingBatch ? null : <CloudUploadIcon />}
          >
            {uploadItems.length > 1 ? `Upload ${uploadItems.length} Files` : 'Upload File'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload/progress notification. Plan parsing is also reflected in DocumentsSection. */}
      {bgJob && ['uploading', 'analyzing', 'done', 'error'].includes(bgJob.status) && (
        <div style={progressPillStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: bgJob.status === 'uploading' || bgJob.status === 'analyzing' ? 8 : 0 }}>
            {bgJob.status === 'error' && <ErrorOutlineIcon style={{ color: '#c62828', fontSize: 20 }} />}
            {bgJob.status === 'uploading' && (
              <CloudUploadIcon style={{ color: '#1976d2', fontSize: 20 }} />
            )}
            {bgJob.status === 'analyzing' && (
              <CloudUploadIcon style={{ color: '#1976d2', fontSize: 20 }} />
            )}
            {bgJob.status === 'done' && (
              <CloudUploadIcon style={{ color: '#2e7d32', fontSize: 20 }} />
            )}

            <span style={{
              flex: 1,
              fontSize: 13,
              fontWeight: 500,
              color: '#333',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {statusLabel}
            </span>

            {bgJob.status === 'done' && bgJob.actionPayload && (
              <button
                className="hmi-btn hmi-btn-sm hmi-btn-primary"
                onClick={() => {
                  if (onUploadComplete) onUploadComplete({ ...bgJob.actionPayload, manualOpen: true })
                  setBgJob(null)
                }}
              >
                {bgJob.actionLabel || 'Open'}
              </button>
            )}

            {/* Dismiss button */}
            {(bgJob.status === 'error' || bgJob.status === 'done') && (
              <IconButton size="small" onClick={() => setBgJob(null)} style={{ padding: 2 }}>
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            )}
          </div>

          {/* Progress bar for upload */}
          {bgJob.status === 'uploading' && (
            <LinearProgress
              variant='determinate'
              value={bgJob.progress}
              style={{ height: 4, borderRadius: 2 }}
            />
          )}
          {bgJob.status === 'analyzing' && (
            <LinearProgress
              variant='indeterminate'
              style={{ height: 4, borderRadius: 2 }}
            />
          )}
        </div>
      )}
    </>
  )
})

export default UploadDropzone
